import { useQuery } from '@tanstack/react-query'
import { useSupabase } from '@/lib/supabase-context'
import { usePermissions } from '@/hooks/usePermissions'
import { useOrganizacion } from '@/core/providers/OrganizacionProvider'
import { gastosReporteService } from '@/services/gastosReporteService'
import type { SupabaseClient } from '@supabase/supabase-js'
import type { Database, VistaSaldoCliente, MovimientoConCliente } from '@/types/database'

export interface DashboardStats {
  totalClientes: number
  clientesActivos: number
  totalDeuda: number
  totalCobrado: number
  saldoPendiente: number
  honorariosMensuales: number
}

/** Meses que muestra el gráfico. Define la ventana de AMBAS series. */
const MESES_GRAFICO = 6

export interface ResumenMensual {
  mes: string
  ingresos: number
  gastos: number
  neto: number
}

export interface DashboardData {
  stats: DashboardStats
  clientesDeudores: VistaSaldoCliente[]
  movimientosRecientes: MovimientoConCliente[]
  resumenMensual: ResumenMensual[]
}

/**
 * ORR-328: combina ingresos y gastos por mes (claves 'YYYY-MM') en la serie que consume el
 * gráfico. Unión de claves — un mes con gasto pero sin cobro (o viceversa) no debe desaparecer —
 * y recorte a los últimos `limite` meses cronológicos. Función pura para poder testearla sin
 * mockear el cliente de Supabase (patrón del repo, ver useObligacionesRecurrentes.test.ts).
 */
export function mergeResumenMensual(
  ingresosPorMes: Record<string, number>,
  gastosPorMes: Record<string, number>,
  limite = MESES_GRAFICO,
): ResumenMensual[] {
  const mesesClaves = Array.from(
    new Set([...Object.keys(ingresosPorMes), ...Object.keys(gastosPorMes)])
  ).sort()

  return mesesClaves.slice(-limite).map(mes => {
    const ingresos = ingresosPorMes[mes] ?? 0
    const gastos = gastosPorMes[mes] ?? 0
    return {
      mes: new Date(`${mes}-01T00:00:00`).toLocaleString('es-AR', { month: 'short', year: 'numeric' }),
      ingresos,
      gastos,
      neto: ingresos - gastos,
    }
  })
}

/**
 * Primer mes ('YYYY-MM') de la ventana del gráfico, contando `meses` hacia atrás incluyendo el
 * actual. Ingresos y gastos arrancan de acá, para que ningún mes entre partido en una serie y
 * entero en la otra. Con componentes locales a propósito: en UTC-3 el `toISOString()` de una
 * noche de fin de mes ya cayó en el mes siguiente y corría la ventana entera.
 */
export function mesInicioVentana(hoy: Date, meses = MESES_GRAFICO): string {
  const inicio = new Date(hoy.getFullYear(), hoy.getMonth() - (meses - 1), 1)
  return `${inicio.getFullYear()}-${String(inicio.getMonth() + 1).padStart(2, '0')}`
}

/** Columnas de la vista que usan las cards y los deudores. Los honorarios van aparte. */
const COLUMNAS_SALDOS = 'id, nombre, responsable, estado, total_debe, total_haber, saldo'

async function fetchDashboardData(
  client: SupabaseClient<Database>,
  organizacionId: string,
  incluirGastos: boolean,
  incluirHonorarios: boolean,
): Promise<DashboardData> {
  // Obtener clientes con saldos — filtrado por organización.
  // Los honorarios son de verHonorariosCliente, no de verMovimientos (ORR-613): sin el flag la
  // columna ni se pide. La vista los suma de cliente_servicio_precios y hasta la fase 2b la base
  // se los devuelve a cualquier miembro.
  const columnas = incluirHonorarios ? `${COLUMNAS_SALDOS}, honorarios_mensuales` : COLUMNAS_SALDOS
  const { data: clientes, error: errorClientes } = await client
    .from('vista_saldos_clientes')
    .select(columnas as '*')
    .eq('organizacion_id', organizacionId)

  if (errorClientes) throw errorClientes

  // Obtener movimientos recientes — filtrado por organización
  const { data: movimientos, error: errorMovimientos } = await client
    .from('movimientos')
    .select(`
      *,
      cliente:clientes(
        id,
        persona:personas(nombre_completo)
      )
    `)
    .eq('organizacion_id', organizacionId)
    // Excluir gastos internos del estudio de "Movimientos recientes" (es una vista de clientes)
    .neq('tipo', 'Gasto')
    .order('fecha', { ascending: false })
    .limit(10)

  if (errorMovimientos) throw errorMovimientos

  // Calcular estadisticas
  const clientesData = (clientes || []) as VistaSaldoCliente[]
  // La vista guarda el estado en minúsculas ('activo'/'inactivo')
  const clientesActivos = clientesData.filter(c => c.estado === 'activo')

  const totalDeuda = clientesData.reduce((acc, c) => acc + (c.total_debe ?? 0), 0)
  const totalCobrado = clientesData.reduce((acc, c) => acc + (c.total_haber ?? 0), 0)
  const saldoPendiente = clientesData.reduce((acc, c) => acc + (c.saldo ?? 0), 0)
  const honorariosMensuales = incluirHonorarios
    ? clientesActivos.reduce((acc, c) => acc + (c.honorarios_mensuales ?? 0), 0)
    : 0

  // Clientes con mayor deuda (top 5) — solo activos (ORR-175)
  const clientesDeudores = clientesData
    .filter(c => c.estado === 'activo' && (c.saldo ?? 0) > 0)
    .sort((a, b) => (b.saldo ?? 0) - (a.saldo ?? 0))
    .slice(0, 5)

  // Un solo borde de mes calendario para las DOS series. Con "180 días atrás" el mes del corte
  // entraba partido en ingresos y entero en gastos, así que su neto salía mal (hallazgo del review
  // de Codex). Se calcula con componentes locales: toISOString() sobre UTC-3 corre el mes de noche.
  const desdeMes = mesInicioVentana(new Date())

  // Obtener ingresos por mes (ventana MESES_GRAFICO) — filtrado por organización
  const { data: movimientosMes, error: errorMes } = await client
    .from('movimientos')
    .select('fecha, haber')
    .eq('organizacion_id', organizacionId)
    .eq('tipo', 'Pago')
    .gte('fecha', `${desdeMes}-01`)

  if (errorMes) throw errorMes

  // Agrupar por mes usando clave yyyy-MM (ordenable); el label se deriva después
  const ingresosPorMes: Record<string, number> = {}
  const movimientosMesData = (movimientosMes ?? []) as { fecha: string; haber: number }[]
  movimientosMesData.forEach(mov => {
    const mes = mov.fecha.slice(0, 7)
    ingresosPorMes[mes] = (ingresosPorMes[mes] ?? 0) + (mov.haber ?? 0)
  })

  // Gastos por mes, misma ventana (ORR-328: comparativo ingresos vs gastos en el dashboard).
  // Solo se piden con verCuentas: Reportes.tsx gatea el mismo dato con ese permiso y no con
  // verReportes, para que un rol con acceso a reportes no vea los gastos internos del estudio.
  // Dos filtros más, ambos por el mismo motivo — que el total sea comparable contra los ingresos:
  //  · categoria_tipo='gasto' — retiros de socios e ingresos internos no son gasto operativo.
  //  · moneda ARS — la vista agrupa por moneda y el gráfico formatea en pesos; sumar un gasto en
  //    USD a su valor nominal inflaría gastos y neto (hoy PROD solo tiene ARS, pero el form
  //    permite cargar USD). Convertir es otra decisión: ver ReporteGastosInternos, que separa.
  const gastosPorMes: Record<string, number> = {}
  if (incluirGastos) {
    const gastosMensuales = await gastosReporteService.obtenerGastosMensuales(client, organizacionId, desdeMes)
    gastosMensuales.forEach(g => {
      if (g.categoria_tipo !== 'gasto' || g.moneda !== 'ARS') return
      gastosPorMes[g.mes_anio] = (gastosPorMes[g.mes_anio] ?? 0) + g.total
    })
  }

  const resumenMensual = mergeResumenMensual(ingresosPorMes, gastosPorMes)

  return {
    stats: {
      totalClientes: clientesData.length,
      clientesActivos: clientesActivos.length,
      totalDeuda,
      totalCobrado,
      saldoPendiente,
      honorariosMensuales,
    },
    clientesDeudores: clientesDeudores as VistaSaldoCliente[],
    movimientosRecientes: (movimientos ?? []) as unknown as MovimientoConCliente[],
    resumenMensual,
  }
}

export function useDashboard() {
  const { supabase, isLoading: supabaseLoading } = useSupabase()
  const { organizacionId } = useOrganizacion()
  const { puede } = usePermissions()
  const verGastos = puede.verCuentas === true
  const verHonorarios = puede.verHonorariosCliente === true

  return useQuery({
    // Los permisos entran en la clave: dos roles distintos no pueden compartir el mismo cache.
    queryKey: ['dashboard', organizacionId, verGastos, verHonorarios],
    queryFn: () => fetchDashboardData(supabase!, organizacionId!, verGastos, verHonorarios),
    enabled: !!supabase && !supabaseLoading && !!organizacionId,
    refetchInterval: 60000, // Refrescar cada minuto
  })
}
